/**
 * diffBarGutter — 3px thin gutter showing local edit markers.
 *
 * Always visible (both blame-on and blame-off modes).
 * Lines with local edits get an amber glow.
 */
import { gutter, GutterMarker } from '@codemirror/view'
import { StateField, StateEffect, type Extension } from '@codemirror/state'
import type { EditorView } from '@codemirror/view'
import type { BlameLine } from '../engine/types'

// ── State management ───────────────────────────────────────────

export const setDiffLinesEffect = StateEffect.define<BlameLine[]>()

const diffLinesField = StateField.define<BlameLine[]>({
    create() { return [] },
    update(state, tr) {
        for (const effect of tr.effects) {
            if (effect.is(setDiffLinesEffect)) {
                return effect.value
            }
        }
        return state
    },
})

// ── Gutter marker ──────────────────────────────────────────────

class DiffLocalMarker extends GutterMarker {
    toDOM(): Node {
        const el = document.createElement('div')
        el.className = 'diff-bar-line diff-local'
        return el
    }
}

class DiffEmptyMarker extends GutterMarker {
    toDOM(): Node {
        const el = document.createElement('div')
        el.className = 'diff-bar-line'
        return el
    }
}

const localMarker = new DiffLocalMarker()
const emptyMarker = new DiffEmptyMarker()

// ── Gutter extension ───────────────────────────────────────────

function diffBarGutterExtension(): Extension {
    return gutter({
        class: 'sb-diff-bar',
        lineMarker(view: EditorView, line) {
            const lines = view.state.field(diffLinesField)
            if (lines.length === 0) return emptyMarker

            const doc = view.state.doc
            const lineNo = doc.lineAt(line.from).number
            const idx = lineNo - 1

            if (idx < 0 || idx >= lines.length) return emptyMarker

            const blameLine = lines[idx]
            const isLocalEdit = !!(blameLine as BlameLine & { local?: boolean }).local
            return isLocalEdit ? localMarker : emptyMarker
        },
        lineMarkerChange(update) {
            return update.transactions.some(tr =>
                tr.effects.some(e => e.is(setDiffLinesEffect))
            )
        },
    })
}

// ── Public API ──────────────────────────────────────────────────

/**
 * Returns the diff-bar gutter extension.
 * Use `setDiffLinesEffect` to update lines from React.
 */
export function diffBarGutter(): Extension {
    return [
        diffLinesField,
        diffBarGutterExtension(),
    ]
}
